// :cow: Cosmose CONFIDENTIAL :iso:
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { Step } from '@med/app/home/demo/guided-tour/models/step';
import { interval, Subject } from 'rxjs';
import { ElementDimensionObserverAggregateService } from '@med/app/home/demo/guided-tour/services/element-dimension-observer-aggregate.service';
import { debounceTime, delay, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { StepPositionService } from '@med/app/home/demo/guided-tour/services/step-position.service';

import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'e-guided-tour-dialog',
  templateUrl: './guided-tour-dialog.component.html',
  styleUrls: ['./guided-tour-dialog.component.scss'],
  providers: [ElementDimensionObserverAggregateService],
  imports: [TranslateModule],
})
export class GuidedTourDialogComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() step: Step;
  @Output() exitDemoClick = new EventEmitter<void>();
  @Output() nextStepClick = new EventEmitter<void>();

  @ViewChild('dialog', {static: false}) dialogElement: ElementRef<HTMLElement>;

  private portalElement: HTMLElement;
  private parentElement: HTMLElement;
  private unsubscribe$ = new Subject<void>();
  private showDialog$ = new Subject<void>();

  constructor(
    private dimensionObserverService: ElementDimensionObserverAggregateService,
    private stepPositionService: StepPositionService,
  ) {
  }

  ngAfterViewInit(): void {
    this.watchDimensionChanges();

    this.showDialog$.pipe(
      debounceTime(100),
      tap(() => {
        if (this.step.portal.isFixed) {
          document.body.parentElement.scrollTo(0, 0);
          document.body.parentElement.style.top = '0';
        }

        this.showDialog();
      }),
      delay(150),
      tap(() => this.scrollView()),
      takeUntil(this.unsubscribe$),
    ).subscribe();

    if (this.dialogElement) {
      this.redraw();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.dimensionObserverService.removeAllObservers();
  }

  ngOnChanges(): void {
    if (this.dialogElement) {
      this.redraw();
    }
  }

  exitDemo(): void {
    this.exitDemoClick.emit();
  }

  onNextClick(): void {
    this.nextStepClick.emit();
  }

  private redraw(): void {
    this.dimensionObserverService.unobserve('element');
    this.dimensionObserverService.unobserve('parentElement');

    interval(100).pipe(
      map(() => document.querySelector(this.step.portal.selector)),
      filter(v => v != null),
      takeUntil(this.unsubscribe$),
      take(1),
    ).subscribe((element: HTMLElement) => {
      this.portalElement = element;
      this.parentElement = document.body.parentElement;
      this.dimensionObserverService.observe('element', this.portalElement);
      this.dimensionObserverService.observe('parentElement', this.parentElement);
      this.showDialog$.next();
    });
  }

  private watchDimensionChanges(): void {
    this.dimensionObserverService.allEvents$.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(() => {
      this.showDialog$.next();
    });
  }

  private showDialog(): void {
    const dialogRect = this.dialogElement.nativeElement.getBoundingClientRect();
    const portalRect = this.portalElement.getBoundingClientRect();
    const containerRect = this.parentElement.getBoundingClientRect();
    let left = this.stepPositionService.getLeftPosition(containerRect, dialogRect, portalRect);
    let top = this.stepPositionService.getTopPosition(containerRect, dialogRect, portalRect);

    if (this.step.dialog.offset?.top) {
      top += this.step.dialog.offset.top;
    }

    if (this.step.dialog.offset?.left) {
      left += this.step.dialog.offset.left;
    }

    this.dialogElement.nativeElement.style.top = `${ top }px`;
    this.dialogElement.nativeElement.style.left = `${ left }px`;
    this.dialogElement.nativeElement.style.opacity = '1';
  }

  private scrollView(): void {
    if (this.step.portal.isFixed) {
      return;
    }

    const portalRect = this.portalElement.getBoundingClientRect();
    const dialogRect = this.dialogElement.nativeElement.getBoundingClientRect();
    const topOffset = Math.min(portalRect.top + document.body.parentElement.scrollTop, dialogRect.top + document.body.parentElement.scrollTop) - 15;
    document.body.parentElement.scrollTo(0, topOffset);
  }
}
